import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Server, FileText, CheckCircle, Clock, AlertCircle, 
  ExternalLink, Copy, ChevronRight, Shield, Globe,
  Building, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDocs = () => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const EnvVariable = ({ name, value, description }) => (
    <div className="p-3 bg-card border border-border rounded flex justify-between items-center">
      <div>
        <code className="text-primary font-mono text-sm">{name}</code>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
        <p className="text-xs text-muted-foreground font-mono">Example: {value}</p>
      </div>
      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(`${name}=${value}`)}>
        <Copy className="w-4 h-4" />
      </Button>
    </div>
  );

  const BrokerCard = ({ name, status, description, pros, cons, link }) => (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg">{name}</CardTitle>
          <Badge className={status === 'recommended' ? 'bg-primary/20 text-primary' : 'bg-secondary'}>
            {status === 'recommended' ? '⭐ Recommended' : status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">{description}</p>
        {pros && (
          <div className="mb-2">
            <p className="text-xs font-heading uppercase text-primary mb-1">Pros</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              {pros.map((pro, i) => <li key={i}>✓ {pro}</li>)}
            </ul>
          </div>
        )}
        {cons && (
          <div className="mb-3">
            <p className="text-xs font-heading uppercase text-destructive mb-1">Cons</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              {cons.map((con, i) => <li key={i}>✗ {con}</li>)}
            </ul>
          </div>
        )}
        {link && (
          <Button variant="outline" size="sm" onClick={() => window.open(link, '_blank')}>
            <ExternalLink className="w-3 h-3 mr-1" /> Visit Website
          </Button>
        )}
      </CardContent>
    </Card>
  );

  const EmailTemplate = ({ subject, body }) => (
    <div className="bg-secondary/30 rounded p-4 relative">
      <Button 
        variant="ghost" 
        size="sm" 
        className="absolute top-2 right-2"
        onClick={() => copyToClipboard(`Subject: ${subject}\n\n${body}`)}
      >
        <Copy className="w-4 h-4 mr-1" /> Copy
      </Button>
      <p className="text-xs text-muted-foreground mb-2">Subject: <span className="text-foreground">{subject}</span></p>
      <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono">{body}</pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="admin-docs">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-8 h-8 text-primary" />
            <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight">
              Admin <span className="text-primary">Documentation</span>
            </h1>
          </div>
          <p className="text-muted-foreground">Broker integration guides, credentials setup, and licensing information</p>
        </motion.div>

        <Tabs defaultValue="brokers" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="brokers">Broker Setup</TabsTrigger>
            <TabsTrigger value="credentials">Credentials</TabsTrigger>
            <TabsTrigger value="licensing">Licensing</TabsTrigger>
            <TabsTrigger value="checklist">Launch Checklist</TabsTrigger>
          </TabsList>

          {/* Brokers Tab */}
          <TabsContent value="brokers">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              {/* Platform Overview */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading uppercase flex items-center gap-2">
                    <Server className="w-5 h-5 text-primary" />
                    Supported Trading Platforms
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 font-heading">Platform</th>
                          <th className="text-left py-2 font-heading">Status</th>
                          <th className="text-left py-2 font-heading">Partnership Required</th>
                          <th className="text-left py-2 font-heading">Best For</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border/50">
                          <td className="py-3">Simulator</td>
                          <td><Badge className="bg-primary/20 text-primary">✅ Ready</Badge></td>
                          <td>No</td>
                          <td>Testing, Demo accounts</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3">MetaTrader 5</td>
                          <td><Badge className="bg-yellow-500/20 text-yellow-400">🔧 Framework Ready</Badge></td>
                          <td>Yes</td>
                          <td>Forex, most common</td>
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3">cTrader</td>
                          <td><Badge className="bg-yellow-500/20 text-yellow-400">🔧 Framework Ready</Badge></td>
                          <td>Yes</td>
                          <td>ECN trading</td>
                        </tr>
                        <tr>
                          <td className="py-3">Match-Trader</td>
                          <td><Badge className="bg-yellow-500/20 text-yellow-400">🔧 Framework Ready</Badge></td>
                          <td>Yes</td>
                          <td>Prop firm white-label</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Broker Cards */}
              <h3 className="font-heading text-xl uppercase mt-8 mb-4">Recommended Brokers</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <BrokerCard 
                  name="Match-Trader"
                  status="recommended"
                  description="Purpose-built white-label solution for prop firms with built-in challenge management."
                  pros={["Built-in evaluation system", "Automatic rule enforcement", "Lower technical complexity", "White-label platform"]}
                  cons={["Partnership required", "Revenue sharing"]}
                  link="https://match-trader.com/"
                />
                <BrokerCard 
                  name="IC Markets (MT5)"
                  status="popular"
                  description="One of the most popular MT5 brokers with excellent spreads and API access."
                  pros={["Low spreads", "Fast execution", "Good API documentation", "Prop firm friendly"]}
                  cons={["Requires business account", "Manager API approval needed"]}
                  link="https://www.icmarkets.com/"
                />
                <BrokerCard 
                  name="Pepperstone (MT5/cTrader)"
                  status="popular"
                  description="Supports both MT5 and cTrader platforms with good institutional support."
                  pros={["Multiple platforms", "Good support", "Competitive pricing"]}
                  cons={["Business documentation required"]}
                  link="https://pepperstone.com/"
                />
                <BrokerCard 
                  name="Eightcap (MT5)"
                  status="prop-friendly"
                  description="Known for being prop firm friendly with straightforward API access."
                  pros={["Prop firm friendly", "Quick onboarding", "Good support"]}
                  cons={["Smaller brand recognition"]}
                  link="https://www.eightcap.com/"
                />
              </div>

              {/* Sample Email */}
              <h3 className="font-heading text-xl uppercase mt-8 mb-4">Sample Broker Outreach Email</h3>
              <EmailTemplate 
                subject="Manager API Access Request for Prop Firm"
                body={`Dear [Broker] Institutional Team,

I am the founder of TRADERSCAPITAL, a proprietary trading firm. We are interested in partnering with [Broker] for our trading operations.

We would like to request:
1. Manager API access for MT5
2. Demo server credentials for testing
3. Information about your white-label or IB programs

Our expected monthly volume: [X lots]
Number of traders: [X traders]

Please let me know the requirements and next steps.

Best regards,
[Your Name]
[Company Name]
[Contact Details]`}
              />
            </motion.div>
          </TabsContent>

          {/* Credentials Tab */}
          <TabsContent value="credentials">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading uppercase">Environment Variables</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    Add these to your <code className="text-primary">/app/backend/.env</code> file when you receive broker credentials.
                  </p>

                  <h4 className="font-heading text-sm uppercase text-primary mt-4">MetaTrader 5</h4>
                  <div className="space-y-2">
                    <EnvVariable name="MT5_SERVER" value="ICMarkets-Demo01" description="Broker server address" />
                    <EnvVariable name="MT5_LOGIN" value="12345678" description="Manager account login" />
                    <EnvVariable name="MT5_PASSWORD" value="your_password" description="Manager account password" />
                  </div>

                  <h4 className="font-heading text-sm uppercase text-primary mt-6">cTrader</h4>
                  <div className="space-y-2">
                    <EnvVariable name="CTRADER_CLIENT_ID" value="your_app_id" description="Application Client ID from connect.spotware.com" />
                    <EnvVariable name="CTRADER_CLIENT_SECRET" value="your_secret" description="Application Secret" />
                    <EnvVariable name="CTRADER_ENVIRONMENT" value="demo" description="'demo' or 'live'" />
                  </div>

                  <h4 className="font-heading text-sm uppercase text-primary mt-6">Match-Trader</h4>
                  <div className="space-y-2">
                    <EnvVariable name="MATCH_TRADER_API_URL" value="https://api.match-trader.com/v1" description="API endpoint URL" />
                    <EnvVariable name="MATCH_TRADER_API_KEY" value="your_api_key" description="API Key from Match-Trader" />
                    <EnvVariable name="MATCH_TRADER_API_SECRET" value="your_secret" description="API Secret" />
                  </div>

                  <h4 className="font-heading text-sm uppercase text-primary mt-6">Platform Selection</h4>
                  <div className="space-y-2">
                    <EnvVariable name="DEFAULT_BROKER_PLATFORM" value="simulator" description="Default broker: simulator, mt5, ctrader, or match_trader" />
                  </div>
                </CardContent>
              </Card>

              {/* Admin Credentials */}
              <Card className="bg-card border-primary/50">
                <CardHeader>
                  <CardTitle className="font-heading uppercase flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Admin Access Credentials
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-secondary/30 rounded p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Admin URL:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-primary font-mono">/admin</code>
                        <Button variant="ghost" size="icon" onClick={() => copyToClipboard('/admin')}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Email:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-primary font-mono">admin@tradercapital.com</code>
                        <Button variant="ghost" size="icon" onClick={() => copyToClipboard('admin@tradercapital.com')}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Password:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-primary font-mono">AdminPass123!</code>
                        <Button variant="ghost" size="icon" onClick={() => copyToClipboard('AdminPass123!')}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-destructive mt-3">⚠️ Change these credentials before going to production!</p>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Licensing Tab */}
          <TabsContent value="licensing">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                {/* Offshore */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="font-heading uppercase flex items-center gap-2">
                      <Globe className="w-5 h-5 text-blue-400" />
                      Offshore
                    </CardTitle>
                    <Badge className="w-fit bg-blue-500/20 text-blue-400">Fast & Low Cost</Badge>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Jurisdictions</p>
                      <p>St. Vincent, Seychelles, Belize, Mauritius</p>
                    </div>
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Cost</p>
                      <p>$2,000 - $5,000</p>
                    </div>
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Timeline</p>
                      <p>2-4 weeks</p>
                    </div>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-primary">✓ Fast setup</p>
                      <p className="text-xs text-primary">✓ Low cost</p>
                      <p className="text-xs text-destructive">✗ Less credibility</p>
                      <p className="text-xs text-destructive">✗ Limited banking</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Regulated */}
                <Card className="bg-card border-primary/50">
                  <CardHeader>
                    <CardTitle className="font-heading uppercase flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      Regulated
                    </CardTitle>
                    <Badge className="w-fit bg-primary/20 text-primary">Most Credible</Badge>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Jurisdictions</p>
                      <p>UK (FCA), Cyprus (CySEC), Australia (ASIC), Dubai (DFSA)</p>
                    </div>
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Cost</p>
                      <p>$50,000 - $500,000+</p>
                    </div>
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Timeline</p>
                      <p>6-12 months</p>
                    </div>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-primary">✓ High credibility</p>
                      <p className="text-xs text-primary">✓ Better banking</p>
                      <p className="text-xs text-destructive">✗ Expensive</p>
                      <p className="text-xs text-destructive">✗ Time consuming</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Education Model */}
                <Card className="bg-card border-yellow-500/50">
                  <CardHeader>
                    <CardTitle className="font-heading uppercase flex items-center gap-2">
                      <Building className="w-5 h-5 text-yellow-400" />
                      Education Model
                    </CardTitle>
                    <Badge className="w-fit bg-yellow-500/20 text-yellow-400">Most Common</Badge>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Structure</p>
                      <p>Skill assessment / educational service</p>
                    </div>
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Cost</p>
                      <p>Varies by jurisdiction</p>
                    </div>
                    <div>
                      <p className="font-heading text-xs uppercase text-muted-foreground">Timeline</p>
                      <p>2-4 weeks</p>
                    </div>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-primary">✓ Most prop firms use this</p>
                      <p className="text-xs text-primary">✓ Not regulated as financial service</p>
                      <p className="text-xs text-destructive">✗ Requires legal review</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-destructive/10 border-destructive/30">
                <CardContent className="p-4">
                  <p className="text-sm text-destructive font-bold">⚠️ Important: Consult a fintech lawyer in your jurisdiction before launching!</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Prop firm regulations vary significantly by country. Get professional legal advice to ensure compliance.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Checklist Tab */}
          <TabsContent value="checklist">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              {[
                {
                  phase: "Phase 1: Testing",
                  timeline: "Week 1-2",
                  items: [
                    { task: "Use Simulator mode", done: true },
                    { task: "Test all trading features", done: true },
                    { task: "Verify challenge/evaluation logic", done: true },
                    { task: "Conduct user testing", done: false }
                  ]
                },
                {
                  phase: "Phase 2: Broker Outreach",
                  timeline: "Week 2-4",
                  items: [
                    { task: "Contact 3-5 MT5 brokers", done: false },
                    { task: "Request demo Manager API access", done: false },
                    { task: "Compare pricing and terms", done: false },
                    { task: "Select primary broker", done: false }
                  ]
                },
                {
                  phase: "Phase 3: Legal Setup",
                  timeline: "Week 2-8",
                  items: [
                    { task: "Consult fintech lawyer", done: false },
                    { task: "Choose jurisdiction", done: false },
                    { task: "Register company", done: false },
                    { task: "Set up bank account", done: false }
                  ]
                },
                {
                  phase: "Phase 4: Integration",
                  timeline: "Week 4-6",
                  items: [
                    { task: "Configure broker credentials", done: false },
                    { task: "Test live account creation", done: false },
                    { task: "Test trading operations", done: false },
                    { task: "Set up monitoring", done: false }
                  ]
                },
                {
                  phase: "Phase 5: Launch",
                  timeline: "Week 6-8",
                  items: [
                    { task: "Soft launch (beta)", done: false },
                    { task: "Monitor for issues", done: false },
                    { task: "Gather feedback", done: false },
                    { task: "Full launch", done: false }
                  ]
                }
              ].map((phase, i) => (
                <Card key={i} className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading uppercase">{phase.phase}</CardTitle>
                      <Badge variant="outline">{phase.timeline}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {phase.items.map((item, j) => (
                        <div key={j} className="flex items-center gap-3 p-2 rounded hover:bg-secondary/30">
                          {item.done ? (
                            <CheckCircle className="w-5 h-5 text-primary" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-muted-foreground" />
                          )}
                          <span className={item.done ? 'text-muted-foreground line-through' : ''}>{item.task}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDocs;
